/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import { useReadOnlyContext } from '../../../context/readOnly'
import { getAncestorNodeIds } from '../../../utils/collapseUtils'

/** Minimal edge shape — satisfied by both React Flow Edge and MindMapEdge */
interface BranchSelectionEdge {
  source: string
  target: string
}

/** Minimal node shape — satisfied by both React Flow Node<ReactFlowNodeData> and MindMapNode */
interface BranchSelectionNode {
  id: string
  data: {
    category?: string
  }
}

const BRANCH_SELECTION_REASON = 'branch_selection'

export interface BranchSelectionActivateOptions {
  /** Node IDs to pre-select when entering selection mode */
  initialNodeIds?: string[]
  /** Called with the final selection when the user confirms. Falls back to console.log if not provided. */
  onConfirm?: (selectedNodeIds: Set<string>) => void
  /** Title shown in the banner when nothing is selected (e.g. "Select branches to export") */
  title?: string
  /** Subtitle shown below the title (e.g. "Click on any node to select it and its ancestors") */
  subtitle?: string
}

export interface BranchSelectionContextValue {
  /** Whether branch selection mode is currently active */
  isActive: boolean
  /** Contextual title for the selection banner */
  bannerTitle: string
  /** Contextual subtitle for the selection banner */
  bannerSubtitle: string
  /** All node IDs in selected branches (clicked nodes + their ancestors) */
  selectedNodeIds: Set<string>
  /** Count of selected nodes per category (e.g. Map { 'idea' => 2, 'need' => 1 }) */
  selectedCategoryCounts: Map<string, number>
  /** Toggle selection of a node (selects it + all ancestors, or deselects it) */
  toggleBranch: (nodeId: string) => void
  /** Enter branch selection mode with optional pre-selection and confirm callback */
  activate: (options?: BranchSelectionActivateOptions) => void
  /** Exit branch selection mode and clear all selections */
  deactivate: () => void
  /** Confirm the current selection and exit selection mode */
  confirmSelection: () => void
}

const BranchSelectionContext = createContext<BranchSelectionContextValue | null>(null)

export function useOptionalBranchSelectionContext(): BranchSelectionContextValue | null {
  return useContext(BranchSelectionContext)
}

interface BranchSelectionProviderProps {
  children: ReactNode
  edges: BranchSelectionEdge[]
  nodes: BranchSelectionNode[]
}

export function BranchSelectionProvider({ children, edges, nodes }: BranchSelectionProviderProps) {
  const { activateReadOnly, deactivateReadOnly } = useReadOnlyContext()
  const [isActive, setIsActive] = useState(false)
  const [clickedIds, setClickedIds] = useState<Set<string>>(new Set())
  const [bannerTitle, setBannerTitle] = useState('Select branches')
  const [bannerSubtitle, setBannerSubtitle] = useState(
    'Click on any node to select it and its ancestors'
  )
  const isActiveRef = useRef(false)
  const selectedNodeIdsRef = useRef<Set<string>>(new Set())
  const edgesRef = useRef(edges)
  edgesRef.current = edges
  const onConfirmRef = useRef<((ids: Set<string>) => void) | null>(null)

  // Derive the full selection: each clicked node + all its ancestors
  const selectedNodeIds = useMemo(() => {
    if (clickedIds.size === 0) return new Set<string>()

    const allIds = new Set<string>()
    for (const clickedId of clickedIds) {
      allIds.add(clickedId)
      for (const ancestorId of getAncestorNodeIds(clickedId, edges)) {
        allIds.add(ancestorId)
      }
    }
    return allIds
  }, [clickedIds, edges])

  // Keep ref in sync so toggleBranch always reads the latest selection
  selectedNodeIdsRef.current = selectedNodeIds

  // Derive category counts from the full selection
  const selectedCategoryCounts = useMemo(() => {
    const counts = new Map<string, number>()
    if (selectedNodeIds.size === 0) return counts

    for (const nodeId of selectedNodeIds) {
      const node = nodes.find((n) => n.id === nodeId)
      if (!node?.data.category) continue
      const category = node.data.category
      counts.set(category, (counts.get(category) ?? 0) + 1)
    }
    return counts
  }, [selectedNodeIds, nodes])

  const activate = useCallback(
    (options?: BranchSelectionActivateOptions): void => {
      if (options?.initialNodeIds && options.initialNodeIds.length > 0) {
        setClickedIds(new Set(options.initialNodeIds))
      }
      onConfirmRef.current = options?.onConfirm ?? null
      setBannerTitle(options?.title ?? 'Select branches')
      setBannerSubtitle(options?.subtitle ?? 'Click on any node to select it and its ancestors')
      setIsActive(true)
      isActiveRef.current = true
      activateReadOnly(BRANCH_SELECTION_REASON)
    },
    [activateReadOnly]
  )

  const deactivate = useCallback((): void => {
    setIsActive(false)
    isActiveRef.current = false
    setClickedIds(new Set())
    onConfirmRef.current = null
    deactivateReadOnly(BRANCH_SELECTION_REASON)
  }, [deactivateReadOnly])

  // Clean up read-only reason if provider unmounts while selection mode is active
  useEffect(() => {
    return () => {
      if (isActiveRef.current) {
        deactivateReadOnly(BRANCH_SELECTION_REASON)
      }
    }
  }, [deactivateReadOnly])

  const confirmSelection = useCallback((): void => {
    const callback = onConfirmRef.current
    if (callback) {
      callback(new Set(selectedNodeIds))
    } else {
      console.log('Selected node IDs:', [...selectedNodeIds])
    }
    deactivate()
  }, [selectedNodeIds, deactivate])

  // Toggle logic:
  //   - Clicked node is in clickedIds → remove it (deselect that branch entirely)
  //   - Clicked node is already selected as an ancestor → "dial back": remove the deeper
  //     clicked nodes that caused this ancestor to be selected, replace with this node
  //   - Otherwise → add to clickedIds (ancestors auto-selected via derivation)
  //
  // Uses refs to always read latest state, keeping the callback stable.
  const toggleBranch = useCallback((nodeId: string): void => {
    setClickedIds((prev) => {
      const currentEdges = edgesRef.current

      // Clicking an explicitly selected node deselects it
      if (prev.has(nodeId)) {
        const next = new Set(prev)
        next.delete(nodeId)
        return next
      }

      // If already selected as an ancestor, "dial back" to this node:
      // find which clicked nodes have this node as an ancestor and replace them
      if (selectedNodeIdsRef.current.has(nodeId)) {
        const next = new Set(prev)
        for (const clickedId of prev) {
          const ancestors = getAncestorNodeIds(clickedId, currentEdges)
          if (ancestors.includes(nodeId)) {
            next.delete(clickedId)
          }
        }
        next.add(nodeId)
        return next
      }

      // New selection: add this node (ancestors auto-included via derivation)
      const next = new Set(prev)
      next.add(nodeId)
      return next
    })
  }, [])

  const value = useMemo(
    (): BranchSelectionContextValue => ({
      isActive,
      bannerTitle,
      bannerSubtitle,
      selectedNodeIds,
      selectedCategoryCounts,
      toggleBranch,
      activate,
      deactivate,
      confirmSelection,
    }),
    [
      isActive,
      bannerTitle,
      bannerSubtitle,
      selectedNodeIds,
      selectedCategoryCounts,
      toggleBranch,
      activate,
      deactivate,
      confirmSelection,
    ]
  )

  return <BranchSelectionContext.Provider value={value}>{children}</BranchSelectionContext.Provider>
}
